import { useState } from "react";
import type { FormEvent } from "react";

import {
  JUSTIFICATIVA_CONFLITOS_ORIENTACAO_UM,
  JUSTIFICATIVA_CONFLITOS_ORIENTACAO_VARIOS,
  JUSTIFICATIVA_CONFLITOS_TITULO_AUSENCIA,
  JUSTIFICATIVA_CONFLITOS_TITULO_UM,
  JUSTIFICATIVA_CONFLITOS_TITULO_VARIOS,
  JUSTIFICATIVA_CONFLITOS_VOLTAR,
  JUSTIFICATIVA_GERAL_MAX,
  justificativaConflitosEscopo,
  SIMULACAO_APLICAR_LABEL,
} from "@/modules/recursos/simulacao";
import { useRolarAteErro } from "../../../hooks/use-rolar-ate-erro";
import { JUSTIFICATIVA_CONFLITOS_OBRIGATORIA } from "./justificativa-conflitos-dialog.constants";
import { ehAusencia, linhaDoConflito } from "./justificativa-conflitos-dialog.utils";
import type {
  ConflitoLinha,
  JustificativaConflitosDialogProps,
  JustificativaConflitosDialogViewModel,
} from "./types-justificativa-conflitos-dialog";

const FORM_ID = "justificativa-conflitos-form";

/**
 * RN011 (HU 2224): quando um "Aplicar na simulação" cria conflitos que não existiam, uma
 * única justificativa cobre todos eles, cada um com a obra atual × a nova alocação
 * (Figma 12041:410015 e 12041:410047).
 */
export function useJustificativaConflitosDialog({
  conflitos,
  obraNova,
  alocacoesNovas,
  erro = null,
  onVoltar,
  onConfirmar,
}: JustificativaConflitosDialogProps): JustificativaConflitosDialogViewModel {
  const [justificativa, setJustificativa] = useState("");
  const [tocada, setTocada] = useState(false);

  const obrigatoria = justificativa.trim() === "" ? JUSTIFICATIVA_CONFLITOS_OBRIGATORIA : null;
  const erroRef = useRolarAteErro(erro);
  const varios = conflitos.length > 1;

  const titulo = varios
    ? JUSTIFICATIVA_CONFLITOS_TITULO_VARIOS
    : conflitos[0] && ehAusencia(conflitos[0])
      ? JUSTIFICATIVA_CONFLITOS_TITULO_AUSENCIA
      : JUSTIFICATIVA_CONFLITOS_TITULO_UM;

  return {
    formId: FORM_ID,
    titulo,
    orientacao: varios
      ? JUSTIFICATIVA_CONFLITOS_ORIENTACAO_VARIOS
      : JUSTIFICATIVA_CONFLITOS_ORIENTACAO_UM,
    voltarLabel: JUSTIFICATIVA_CONFLITOS_VOLTAR,
    confirmarLabel: SIMULACAO_APLICAR_LABEL,
    conflitos: conflitos.flatMap((conflito, indice) => {
      const linha = linhaDoConflito(conflito, indice, obraNova, alocacoesNovas);
      return linha ? [linha] : [];
    }) satisfies readonly ConflitoLinha[],
    escopo: justificativaConflitosEscopo(conflitos.length),
    erro,
    erroRef,
    justificativa: {
      value: justificativa,
      error: tocada ? obrigatoria : null,
      maxLength: JUSTIFICATIVA_GERAL_MAX,
      digitar: setJustificativa,
    },
    confirmar: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setTocada(true);
      if (obrigatoria) return;
      onConfirmar(justificativa.trim());
    },
    voltar: onVoltar,
  };
}
